import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';
import { isFirefox } from 'react-device-detect';

import { Box, CircularProgress, Tooltip, TableCell, Typography } from '@mui/material';

// api / models
import EventApi from '../../api/event.api';
import RoomApi from '../../api/room.api';
import BuildingApi from '../../api/building.api';
import {Event, EventFormState} from '../../models/event';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';

// comps
import GlobalButton from '../../components/global/button';
import GlobalInput from '../../components/global/input';
import FilterContainer from '../../components/layout/filterContainer';
import ContentContainer from '../../components/layout/contentContainer';
import DataGrid from '../../components/grid/dataGrid';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import { ReactComponent as EventIcon } from '../../assets/icons/event-icon.svg';

// constants
import { ACADEMIC_VIEW_TYPE, accountTypes, FACILITIES_VIEW_TYPE } from '../../constants';
import {
    MOMENT_DATE_DISPLAY_FORMAT,
    MOMENT_DATE_FORMAT,
    MOMENT_TIME_DISPLAY_FORMAT,
    MOMENT_TIME_FORMAT
} from '../../constants/date_format';
import {CustomTableInputCell} from "../../components/grid/CustomTableCell";
import BannerEventDetails from "./bannerEventDetails";
import MEventApi from "../../api/mevent.api";
import GlobalCheckbox from "../../components/global/checkbox";
import GlobalDropdown from "../../components/global/dropdown";
import CampusApi from "../../api/campus.api";
import {setEventCRN} from "../../store/reducers/app";

const Events = (props: any) => {
    const dispatch = useAppDispatch();
    const _eventApi = new EventApi();
    const _roomApi = new RoomApi();
    const _buildingApi = new BuildingApi();
    const _mEventApi = new MEventApi();
    const _campusApi = new CampusApi();

    const appState = useAppSelector((state) => state.appState);
    const user = useAppSelector((state) => state.user);
    const { viewType, campus, eventCRN } = appState;
    const isCentralTimetabler = user.accountType === accountTypes.centralTimetabler;
    const [resetPage, setResetPage] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    let padding = '5px';

    const [state, setState] = useState({
        eventData: null as EventFormState,
        roomData: [],
        buildingData: [],
        showLoading: false,
        isClearAll: false,
        isFilteredGrid: false,
        openBannerEventDetails: false,
        bannerEvent: null,
        managedEvent: null,
        staffs: null,
        clients: null,
        eventStatuses: null,
        itemCategories: null,
        items: null,
        rateCategories: null,
        campuses: null,
        buildings: null,
        selectedEventId: null,
    });

    const [filterObject, setFilterObject] = useState({
        eventCrn: null,
        name: null,
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().add(1, 'years').format('YYYY-MM-DD'),
        timeIn: '00:00',
        timeOut: '23:59',
        building: null,
        room: null,
        contactName: null,
        note: null,
        comment: null,
        managedEventOnly: false,
        unmatchedManagedEventOnly: false,
        campus: campus
    });
    
    const [buttonDisabled, setButtonDisabled] = useState(false);

    const loadCampuses = async () => {
        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            const campuses = await _campusApi.GetCampuses();

            setState(prevState => ({
                ...prevState,
                campuses: campuses
            }));
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    };

    const loadAllBuildings = async (campus: any) => {
        // No campus selected (e.g. Central Timetabler / Academic views start with none) -
        // there is nothing to load, so clear the building list instead of dereferencing null.
        if (!campus?.id) {
            setState(prevState => ({ ...prevState, buildings: [] }));
            return;
        }

        setState(prevState => ({ ...prevState, showLoading: true }));
        try {
            const buildings = await _buildingApi.GetAllBuildings(campus.id);

            if (campus.id === 1) {
                buildings.unshift({id: -1, name: "All Buildings"})
            }

            setState(prevState => ({
                ...prevState,
                buildings: buildings
            }));

            if (buildings.length === 1) {
                setFilterObject({
                    ...filterObject,
                    building: buildings[0],
                })
            }
        } finally {
            setState(prevState => ({ ...prevState, showLoading: false }));
        }
    };
    
    const handleCachedEventCRN = async (eventCRN: string) => {
        setState(prevState => ({
            ...prevState,
            showLoading: true
        }))
        try {
            const bannerEvent = await _mEventApi.GetBannerEventsByEventCrn(eventCRN)
            const managedEvent = await _mEventApi.GetManagedEventsByEventCrn(eventCRN)
            const staffs = await _mEventApi.GetStaffs()
            const clients = await _mEventApi.GetClientsByActivity(true)
            const eventStatuses = await _mEventApi.GetEventStatuses()
            const itemCategories = await _mEventApi.GetItemCategories()
            const items = await _mEventApi.GetItems()
            const rateCategories = await _mEventApi.GetRateCategories();

            setState(prevState => ({
                ...prevState,
                openBannerEventDetails: true,
                managedEvent,
                bannerEvent,
                staffs,
                clients,
                eventStatuses,
                itemCategories,
                items,
                rateCategories
            }))
        } finally {
            setState(prevState => ({
                ...prevState,
                showLoading: false
            }))
        }
    }
    
    useEffect(() => {
        loadCampuses()
        
        if (eventCRN) {
            handleCachedEventCRN(eventCRN);
        }
    }, []);

    useEffect(() => {
        setFilterObject(prevFilter => ({
            ...prevFilter,
            building: null
        }))

        loadAllBuildings(filterObject?.campus);
    }, [filterObject.campus]);

    useEffect(() => {
        if (!Object.values(filterObject).every((o) => o === '' || o === '00:00' || o === '23:59' || !o)) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [filterObject]);
    
    useEffect(() => {
        setFilterObject(prevFilter => ({
            ...prevFilter,
            campus: campus ?? null,
        }))
    }, [campus])

    const loadData = async (startDate: Date, endDate: Date, campusId?: number, buildingId?: number, managedEventOnly?: boolean, unmatchedManagedEventOnly?: boolean) => {
        setState({ ...state, showLoading: true });
        try {
            const eventData = await _eventApi.GetEvents(startDate, endDate, campusId, buildingId, managedEventOnly, unmatchedManagedEventOnly);
            const roomData = await _roomApi.GetRooms();
            const buildingData = await _buildingApi.GetAllBuildings(campusId);
            const eventFormData = new EventFormState(eventData, roomData, buildingData);

            setState({
                ...state,
                roomData,
                buildingData,
            });
            
            return eventFormData;
        } finally {
            setState({ ...state, showLoading: false });
        }
    };
    
    const renderEventGrid = (data: any, onChange: any, handleInputFocus: any) => {
        const { eventCrn, name, date, timeIn, timeOut, building, room, contactName, note, instanceNote, dateUnmatched } = data;
        const noteLength = screenWidth <= 1480 ? 30 : 55;
        return (
            <>
                <TableCell sx={{ width: '4%' }}>{eventCrn}</TableCell>
                <TableCell sx={{ width: '17%' }}>{name}</TableCell>
                <TableCell sx={{ width: '2%' }}>{data.isManagedEvent ? (<EventIcon/>) : (<></>)}</TableCell>
                <TableCell sx={{ width: '10%', textAlign: 'center' }}>{date.format(MOMENT_DATE_FORMAT)}</TableCell>
                <TableCell sx={{ width: '7%' }}>{timeIn.format(MOMENT_TIME_DISPLAY_FORMAT)}</TableCell>
                <TableCell sx={{ width: '8%' }}>{timeOut.format(MOMENT_TIME_DISPLAY_FORMAT)}</TableCell>
                <TableCell sx={{ width: '5%' }}>{building?.name}</TableCell>
                <TableCell sx={{ width: '4%' }}>{room?.number}</TableCell>
                <TableCell sx={{ width: '10%' }}>{contactName}</TableCell>
                <TableCell sx={{ width: '17%' }}>
                    <Tooltip title={note?.length > noteLength ? note : ''}>
                        <Typography variant="body2">
                            {note?.slice(0, noteLength)} {note?.length > noteLength && ' ...'}
                        </Typography>
                    </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '10%' }}>{dateUnmatched && dateUnmatched.format(MOMENT_DATE_DISPLAY_FORMAT)}</TableCell>
            </>
        );
    };

    const handleUpdateComment = async (dataItem: any) => {
        try {
            await _eventApi.PatchEvent(dataItem.id, dataItem.instanceNote);
            toast.success(`Comment for ${dataItem.eventCrn} ${dataItem.name} has been successfully saved.`);
            return true;
        } catch (error) {
            toast.error(error);
            return false;
        }
    };

    const handleFitlerObj = (name: any, value: any) => {
        if (value === '') value = null;
        setFilterObject({
            ...filterObject,
            [name]: value,
        });
        setButtonDisabled(false);

        // state.eventData.filterGridData(filterObject);
    };

    const handleClearAll = () => {
        let filteredObject = { ...filterObject };
        Object.keys(filteredObject).forEach((i) => {
            if (i === 'timeIn') {
                return (filteredObject[i] = '00:00');
            } else if (i === 'timeOut') {
                return (filteredObject[i] = '23:59');
            } else if (i === 'managedEventOnly') {
                return (filteredObject[i] = false);
            } else if (i === 'unmatchedManagedEventOnly') {
                return (filteredObject[i] = false);
            } else if (i === 'startDate') {
                return (filteredObject[i] = moment().subtract(1, 'months').format('YYYY-MM-DD'))
            } else if (i === 'endDate') {
                return (filteredObject[i] = moment().add(3, 'months').format('YYYY-MM-DD'))
            }

            return (filteredObject[i] = '');
        });

        // state.eventData.filterGridData(filteredObject);

        setState({
            ...state,
            isClearAll: true,
            isFilteredGrid: false,
            eventData: null
        });
        setFilterObject(filteredObject);
        setResetPage(true);
        // setButtonDisabled(true);
    };

    const handleClickRow = (dataItem) => {
        setState({
            ...state,
            openBannerEventDetails: true
        });
    };

    const handleDetailsPageClose = () => {
        dispatch(setEventCRN(null));
        setState({
            ...state,
            openBannerEventDetails: false,
        });
    };

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const handleFilteredData = () => {
        const { startDate, endDate, timeIn, timeOut, managedEventOnly, unmatchedManagedEventOnly, campus, building } = filterObject;
        
        if (!campus) {
            toast.error('Campus must be selected');
            return;
        }
        if (!building) {
            toast.error('Building must be selected');
            return;
        }
        if (startDate) {
            if (moment(endDate).isBefore(startDate)) {
                toast.error('Selected date is past Start Date.');
                return;
            }
        }
        if (timeOut?.length >= 4 && moment(timeOut, MOMENT_TIME_FORMAT).isBefore(moment(timeIn, MOMENT_TIME_FORMAT))) {
            toast.error('End Time must be AFTER Start Time.');
            return;
        }

        if (timeIn?.length >= 4 && moment(timeIn, MOMENT_TIME_FORMAT).isAfter(moment(timeOut, MOMENT_TIME_FORMAT))) {
            toast.error('Start Time must be BEFORE End Time.');
            return;
        }
        const filteredObject = { ...filterObject };
        
        loadData(moment(startDate).toDate(), moment(endDate).toDate(), campus.id, building.id, managedEventOnly, unmatchedManagedEventOnly)
            .then((eventFormData) => {
                
                eventFormData.filterGridData(filteredObject);
                setState({
                    ...state,
                    eventData: eventFormData,
                    isFilteredGrid: true,
                });
            })
            .catch((error) => toast.error(error));
    };

    return (
        <>
            {!state.openBannerEventDetails ? (
                <>
                    <FilterContainer sx={{ flexDirection: 'column', p: '7px' }}>
                        <FlexBox sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <FlexBox sx={{ mt: viewType === FACILITIES_VIEW_TYPE && '10px', flexDirection: 'column' }}>
                                <FlexBox>
                                    <GlobalInput
                                        label="filter"
                                        icon={<FilterIcon />}
                                        placeholder="Event#"
                                        name={'eventCrn'}
                                        value={filterObject?.eventCrn}
                                        onChange={handleFitlerObj}
                                        sx={{ width: '85px' }}
                                        disabled={state.showLoading}
                                    />
                                    <GlobalInput placeholder="Event Title Header" name={'name'} value={filterObject?.name} onChange={handleFitlerObj} disabled={state.showLoading} />
                                    <GlobalInput placeholder="Contact Name" name={'contactName'} value={filterObject?.contactName} onChange={handleFitlerObj} disabled={state.showLoading} />
                                    <GlobalInput placeholder="Description" name={'note'} value={filterObject?.note} onChange={handleFitlerObj} sx={{ width: '220px' }} disabled={state.showLoading} />
                                    <GlobalDropdown
                                        placeholder={"Select Campus"}
                                        name={'name'}
                                        field={'id'}
                                        dataItemKey={'campus'}
                                        value={filterObject?.campus}
                                        data={state.campuses ? state.campuses : []}
                                        onChange={handleFitlerObj}
                                        disabled={state.showLoading}
                                    />
                                    <GlobalDropdown
                                        placeholder={"Select Building"}
                                        name={'name'}
                                        field={'id'}
                                        isSearch
                                        dataItemKey={'building'}
                                        value={filterObject?.building}
                                        data={state.buildings ? state.buildings : []}
                                        onChange={handleFitlerObj}
                                        disabled={state.showLoading || !filterObject?.campus || filterObject?.campus?.id !== 1}
                                    />
                                    <GlobalInput placeholder="Room" name={'room'} value={filterObject?.room} onChange={handleFitlerObj} sx={{ width: '120px' }} disabled={state.showLoading} />
                                </FlexBox>
                                <FlexBox sx={{ mt: '10px' }}>
                                    <GlobalInput
                                        label="Start Date"
                                        placeholder="Start Date"
                                        type="date"
                                        name={'startDate'}
                                        value={filterObject?.startDate}
                                        onChange={handleFitlerObj}
                                        sx={{ width: '155px' }}
                                        disabled={state.showLoading}
                                    />
                                    <GlobalInput
                                        label="End Date"
                                        placeholder="End Date"
                                        type="date"
                                        name={'endDate'}
                                        value={filterObject?.endDate}
                                        onChange={handleFitlerObj}
                                        sx={{ width: '155px' }}
                                        disabled={state.showLoading}
                                    />
                                    <GlobalInput label="Time In" isTime={true} name={'timeIn'} value={filterObject?.timeIn} onChange={handleFitlerObj} sx={{ width: '85px' }} disabled={state.showLoading} />
                                    <GlobalInput label="Time Out" isTime={true} name={'timeOut'} value={filterObject?.timeOut} onChange={handleFitlerObj} sx={{ width: '85px' }} disabled={state.showLoading} />
                                    <GlobalCheckbox label={"Managed Only"} onChange={handleFitlerObj} name={'managedEventOnly'} isChecked={filterObject?.managedEventOnly} useCheckedValue={true} />
                                    <GlobalCheckbox label={"UnMatched Only"} onChange={handleFitlerObj} name={'unmatchedManagedEventOnly'} isChecked={filterObject?.unmatchedManagedEventOnly} useCheckedValue={true} />
                                </FlexBox>
                            </FlexBox>
                            <FlexBox sx={{ mt: '10px', width: '10%', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                                <GlobalButton variant="round" text="clear all" fullWidth secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} disabled={buttonDisabled || state.showLoading} />
                                <GlobalButton variant="round" text="search" sx={{ marginTop: '5px' }} onClick={handleFilteredData} disabled={buttonDisabled || state.showLoading} />
                            </FlexBox>
                        </FlexBox>
                    </FilterContainer>
                    {state.showLoading && (
                        <FlexBox
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 10,
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <CircularProgress size={100} />
                        </FlexBox>
                    )}
                    {(!state.showLoading &&
                        state.eventData?.gridData?.length > 0 && (
                            <DataGrid
                                isEdit={viewType === FACILITIES_VIEW_TYPE}
                                // isScrolling
                                clickable={!isCentralTimetabler}
                                resetPage={resetPage}
                                topHeight={80}
                                padding={padding}
                                data={state.eventData?.gridData}
                                page={currentPage}
                                onPageChange={setCurrentPage}
                                onResetPage={() => setResetPage(false)}
                                columns={eventGridColumns}
                                renderRows={renderEventGrid}
                                isClearSorting={state.isClearAll}
                                onHeaderClick={() => {
                                    setButtonDisabled(false);
                                }}
                                onSaveAsync={(dataItem: any) => handleUpdateComment(dataItem)}
                                dataItemKey={'id'}
                                activeCell={state.selectedEventId ? { id: state.selectedEventId } : null}
                                onRowClick={async (item: any) => {
                                    setState(prevState => ({
                                        ...prevState,
                                        showLoading: true
                                    }))
                                    try {
                                        const bannerEvent = await _mEventApi.GetBannerEventsByEventCrn(item.eventCrn)
                                        const managedEvent = await _mEventApi.GetManagedEventsByEventCrn(item.eventCrn)
                                        const staffs = await _mEventApi.GetStaffs()
                                        const clients = await _mEventApi.GetClientsByActivity(true)
                                        const eventStatuses = await _mEventApi.GetEventStatuses()
                                        const itemCategories = await _mEventApi.GetItemCategories()
                                        const items = await _mEventApi.GetItems()
                                        const rateCategories = await _mEventApi.GetRateCategories();

                                        dispatch(setEventCRN(managedEvent.eventCRN ?? bannerEvent.eventCRN))
                                        setState(prevState => ({
                                            ...prevState,
                                            selectedEventId: item.id,
                                            openBannerEventDetails: true,
                                            managedEvent,
                                            bannerEvent,
                                            staffs,
                                            clients,
                                            eventStatuses,
                                            itemCategories,
                                            items,
                                            rateCategories
                                        }))
                                    } finally {
                                        setState(prevState => ({
                                            ...prevState,
                                            showLoading: false
                                        }))
                                    }
                                }}
                            />
                        )
                    )}
                </>
            ) : (
                <BannerEventDetails
                    event={state.managedEvent ? state.managedEvent : state.bannerEvent}
                    staffs={state.staffs}
                    clients={state.clients}
                    eventStatuses={state.eventStatuses}
                    itemCategories={state.itemCategories}
                    onClose={handleDetailsPageClose}
                    items={state.items}
                    rateCategories={state.rateCategories}
                />
            )}
        </>
    );
};

export default Events;

const FlexBox = styled(Box)`
    && {
        display: flex;

        & > * {
            margin-right: 10px;
        }

        & > :last-child {
            margin-right: 0px;
        }
    }
`;

const eventGridColumns = [
    {
        id: 'eventCrn',
        label: 'Event#',
    },
    {
        id: 'name',
        label: 'Event Title Header',
    },
    {
        id: '',
        label: '',
        disabled: true
    },
    {
        id: 'date',
        label: 'Date Scheduled',
        sx: {
            textAlign: 'center',
        },
    },
    {
        id: 'timeIn',
        label: 'Time In',
    },
    {
        id: 'timeOut',
        label: 'Time Out',
    },
    {
        id: 'building',
        label: 'Building',
        objectId: 'name'
    },
    {
        id: 'room',
        label: 'Room',
        objectId: 'number'
    },
    {
        id: 'contactName',
        label: 'Contact Name',
    },
    {
        label: 'Description',
        disabled: true,
    },
    {
        id: 'dateUnmatched',
        label: 'Date Unmatched'
    }
];
